/**
 * Cookie Management Utility
 * 
 * Handles all cookie operations with explicit user consent
 * No cookies are stored until user accepts or configures preferences
 */

// Extract GA ID from environment variable
const GA_ID = process.env.REACT_APP_GA_ID || 'G-XXXXXXXXXX';
const GA_SESSION_COOKIE = `_ga_${GA_ID.replace(/[^A-Z0-9]/g, '')}`;

/**
 * Cookie types defined in the application
 */
export const COOKIE_TYPES = {
  LANGUAGE: 'language',
  GA_ID: '_ga',
  GA_SESSION: GA_SESSION_COOKIE,
  COOKIE_CONSENT: 'cookieConsent',
  COOKIE_PREFERENCES: 'cookiePreferences',
};

/**
 * Default cookie preferences (all disabled)
 * Users must explicitly enable each type
 */
const DEFAULT_PREFERENCES = {
  necessary: true, // Always enabled (cannot be disabled)
  functional: false, // For language cookie
  analytics: false, // For Google Analytics
};

/**
 * Get a specific cookie value by name
 * @param {string} name - Cookie name
 * @returns {string|null} Cookie value or null if not found
 */
export const getCookie = (name) => {
  try {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(new RegExp(`(^|;)\\s*${name}=([^;]+)`));
    return match ? decodeURIComponent(match[2]) : null;
  } catch (e) {
    console.error(`[Cookie] Error reading cookie ${name}:`, e);
    return null;
  }
};

/**
 * Set a cookie (internal use only - should be called after checking preferences)
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {number} days - Days until expiration (optional)
 */
const setCookieInternal = (name, value, days = 365) => {
  try {
    if (typeof document === 'undefined') return;
    let expires = '';
    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = '; expires=' + date.toUTCString();
    }
    document.cookie = `${name}=${encodeURIComponent(value)}${expires}; path=/`;
    console.log(`[Cookie] Set cookie: ${name}`);
  } catch (e) {
    console.error(`[Cookie] Error setting cookie ${name}:`, e);
  }
};

/**
 * Delete a cookie
 * @param {string} name - Cookie name to delete
 */
export const deleteCookie = (name) => {
  try {
    if (typeof document === 'undefined') return;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
    console.log(`[Cookie] Deleted cookie: ${name}`);
  } catch (e) {
    console.error(`[Cookie] Error deleting cookie ${name}:`, e);
  }
};

/**
 * Get current cookie preferences
 * Returns null if user hasn't set preferences yet
 * @returns {object|null} Cookie preferences or null if not set
 */
export const getCookiePreferences = () => {
  try {
    const prefs = getCookie(COOKIE_TYPES.COOKIE_PREFERENCES);
    return prefs ? JSON.parse(prefs) : null;
  } catch (e) {
    console.error('[Cookie] Error parsing cookie preferences:', e);
    return null;
  }
};

/**
 * Check if user has accepted cookies
 * @returns {boolean} True if user has made a choice, false if no preferences set
 */
export const hasCookieConsent = () => {
  return getCookie(COOKIE_TYPES.COOKIE_CONSENT) !== null;
};

/**
 * Check if a specific cookie type is enabled
 * @param {string} cookieType - Cookie type to check (e.g., 'functional', 'analytics')
 * @returns {boolean} True if enabled, false otherwise
 */
export const isCookieTypeEnabled = (cookieType) => {
  const preferences = getCookiePreferences();
  if (!preferences) return false;
  return preferences[cookieType] === true;
};

/**
 * Accept all cookies
 * Sets all preferences to true and stores consent
 */
export const acceptAllCookies = () => {
  try {
    const preferences = {
      ...DEFAULT_PREFERENCES,
      functional: true,
      analytics: true,
    };
    
    // Store preferences
    setCookieInternal(COOKIE_TYPES.COOKIE_PREFERENCES, JSON.stringify(preferences), 365);
    
    // Store consent
    setCookieInternal(COOKIE_TYPES.COOKIE_CONSENT, 'true', 365);
    
    console.log('[Cookie] All cookies accepted');
  } catch (e) {
    console.error('[Cookie] Error accepting all cookies:', e);
  }
};

/**
 * Reject optional cookies
 * Keeps only necessary cookies and stores rejection
 */
export const rejectAllCookies = () => {
  try {
    const preferences = {
      ...DEFAULT_PREFERENCES,
      functional: false,
      analytics: false,
    };
    
    // Store preferences
    setCookieInternal(COOKIE_TYPES.COOKIE_PREFERENCES, JSON.stringify(preferences), 365);
    
    // Store consent (user made a choice to reject)
    setCookieInternal(COOKIE_TYPES.COOKIE_CONSENT, 'false', 365);
    
    // Delete optional cookies that may have been set
    deleteCookie(COOKIE_TYPES.LANGUAGE);
    deleteCookie(COOKIE_TYPES.GA_ID);
    deleteCookie(COOKIE_TYPES.GA_SESSION);
    
    console.log('[Cookie] Optional cookies rejected');
  } catch (e) {
    console.error('[Cookie] Error rejecting cookies:', e);
  }
};

/**
 * Update cookie preferences with custom settings
 * @param {object} preferences - New preferences object
 *   - necessary: boolean (always true, cannot be changed)
 *   - functional: boolean (for language and functional cookies)
 *   - analytics: boolean (for Google Analytics)
 */
export const updateCookiePreferences = (preferences) => {
  try {
    const validatedPreferences = {
      necessary: true, // Always necessary
      functional: preferences.functional === true,
      analytics: preferences.analytics === true,
    };
    
    // Store new preferences
    setCookieInternal(COOKIE_TYPES.COOKIE_PREFERENCES, JSON.stringify(validatedPreferences), 365);
    
    // Store consent
    setCookieInternal(COOKIE_TYPES.COOKIE_CONSENT, 'true', 365);
    
    // Clean up optional cookies based on new preferences
    if (!validatedPreferences.functional) {
      deleteCookie(COOKIE_TYPES.LANGUAGE);
    }
    if (!validatedPreferences.analytics) {
      deleteCookie(COOKIE_TYPES.GA_ID);
      deleteCookie(COOKIE_TYPES.GA_SESSION);
    }
    
    console.log('[Cookie] Cookie preferences updated:', validatedPreferences);
  } catch (e) {
    console.error('[Cookie] Error updating cookie preferences:', e);
  }
};

/**
 * Set language cookie (only if functional cookies are enabled)
 * @param {string} language - Language code (e.g., 'th', 'en')
 * @returns {boolean} True if cookie was set, false if not allowed
 */
export const setLanguageCookie = (language) => {
  try {
    if (!isCookieTypeEnabled('functional')) {
      console.log('[Cookie] Cannot set language cookie - functional cookies not enabled');
      return false;
    }
    
    setCookieInternal(COOKIE_TYPES.LANGUAGE, language, 365);
    return true;
  } catch (e) {
    console.error('[Cookie] Error setting language cookie:', e);
    return false;
  }
};

/**
 * Get language from cookie (only if functional cookies are enabled)
 * @returns {string|null} Language code or null if not set or not allowed
 */
export const getLanguageCookie = () => {
  try {
    if (!isCookieTypeEnabled('functional')) {
      return null;
    }
    return getCookie(COOKIE_TYPES.LANGUAGE);
  } catch (e) {
    console.error('[Cookie] Error getting language cookie:', e);
    return null;
  }
};

/**
 * Set Google Analytics cookies immediately
 * Creates both _ga and _ga_<ID> cookies with placeholder values
 * This allows GA to work immediately without waiting for page reload
 * @returns {boolean} True if cookies were set, false otherwise
 */
export const setAnalyticsCookies = () => {
  try {
    if (!isCookieTypeEnabled('analytics')) {
      console.log('[Cookie] Cannot set analytics cookies - analytics cookies not enabled');
      return false;
    }
    
    // Create GA ID cookie with a unique value
    const gaValue = `GA1.1.${Math.random().toString(36).substring(2, 15)}.${Math.floor(Date.now() / 1000)}`;
    setCookieInternal(COOKIE_TYPES.GA_ID, gaValue, 730); // 2 years
    
    // Create GA session cookie with a placeholder (will be updated by gtag)
    const gaSessionValue = `${Math.floor(Math.random() * 1000000000)}`;
    setCookieInternal(COOKIE_TYPES.GA_SESSION, gaSessionValue, 0); // Session cookie
    
    console.log('[Cookie] Google Analytics cookies set:', { [COOKIE_TYPES.GA_ID]: gaValue, [COOKIE_TYPES.GA_SESSION]: gaSessionValue });
    return true;
  } catch (e) {
    console.error('[Cookie] Error setting analytics cookies:', e);
    return false;
  }
};

/**
 * Initialize Google Analytics cookies (only if analytics cookies are enabled)
 * Should be called after user accepts analytics cookies
 * @returns {boolean} True if allowed to set analytics, false otherwise
 */
export const enableAnalyticsCookies = () => {
  try {
    if (!isCookieTypeEnabled('analytics')) {
      console.log('[Cookie] Cannot enable analytics - analytics cookies not enabled');
      return false;
    }
    console.log('[Cookie] Analytics cookies enabled');
    return true;
  } catch (e) {
    console.error('[Cookie] Error enabling analytics cookies:', e);
    return false;
  }
};

/**
 * Clear all cookies (for debugging or user account deletion)
 * WARNING: This removes all application cookies
 */
export const clearAllCookies = () => {
  try {
    Object.values(COOKIE_TYPES).forEach(name => {
      deleteCookie(name);
    });
    console.log('[Cookie] All cookies cleared');
  } catch (e) {
    console.error('[Cookie] Error clearing all cookies:', e);
  }
};
